import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useLazyGetChartImageQuery } from '@/store/api/BasicChart/chartImagesApi';
import Modal from '@/utils/modal.utils';

interface BirthDetails {
  date: string;
  time: string;
  place: string;
  fullName: string;
  gender: 'Male' | 'Female';
}

interface DasamsaTableProps {
  birthDetails: BirthDetails;
  dasamsa: any[];
}

export default function DasamsaTable({ birthDetails, dasamsa }: DasamsaTableProps): JSX.Element {
  const [params] = useSearchParams();
  const userId = params.get('userId');

  const [charts, setCharts] = useState<Record<string, any>>({});
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImages, setModalImages] = useState<string[]>([]);
  const [selectedTitle, setSelectedTitle] = useState('');

  const [fetchChartImage] = useLazyGetChartImageQuery();

  const openModal = (key: string) => {
    const chartId = key.split('-')[0];
    const south = charts[`${chartId}-South`]?.chartUrl;
    const north = charts[`${chartId}-North`]?.chartUrl;

    setModalImages([south, north].filter(Boolean));
    setSelectedTitle('Dasamsa Chart (South Indian & North Indian)');
    setModalOpen(true);
  };

  useEffect(() => {
    if (!userId) return;

    async function fetchCharts() {
      const payload = {
        userId: String(userId),
        imageType: 'png' as const,
      };

      const south = await fetchChartImage({
        ...payload,
        chartId: 'D10',
        chartType: 'south',
      }).unwrap();

      const north = await fetchChartImage({
        ...payload,
        chartId: 'D10',
        chartType: 'north',
      }).unwrap();

      setCharts({
        'D10-South': south,
        'D10-North': north,
      });
    }

    fetchCharts();
  }, [birthDetails.date, birthDetails.time, birthDetails.place, fetchChartImage]);

  return (
    <div className="rounded-xl">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 w-full mx-auto">
        <div className="col-span-1 flex flex-col justify-between border border-[#2DAAFD] p-6 gap-4 rounded-xl">
          <div className="grid grid-cols-1 gap-4">
            {charts['D10-South']?.chartUrl && (
              <div>
                <p className="text-center font-semibold font-spectral"> DASAMSA CHART</p>
                <img
                  src={charts['D10-South'].chartUrl}
                  className="w-full rounded-md border p-[8px] border-[#2DAAFD] cursor-pointer"
                  onClick={() => openModal('D10-South')}
                />
              </div>
            )}

            {charts['D10-North']?.chartUrl && (
              <div>
                <p className="text-center font-semibold font-spectral">DASAMSA CHART</p>
                <img
                  src={charts['D10-North'].chartUrl}
                  className="w-full rounded-md border border-[#2DAAFD] cursor-pointer"
                  onClick={() => openModal('D10-North')}
                />
              </div>
            )}
          </div>
        </div>

        <div className="col-span-3 h-fit border border-[#2DAAFD] rounded-xl p-4 overflow-x-auto font-lato">
          <h3 className="font-32 font-semibold font-spectral mb-4 bg-clip-text text-transparent bg-gradient-to-r from-[#082261] to-[#34065c] text-center">
            DASAMSA TABLE
          </h3>

          <table className="w-full border-collapse border border-[#2DAAFD] font-18 whitespace-nowrap">
            <thead className="bg-[#001B6B] text-white font-spectral">
              <tr>
                <th className="border px-3 py-2">Planet</th>
                <th className="border px-3 py-2">Sign</th>
                <th className="border px-3 py-2">House</th>
                <th className="border px-3 py-2">Nakshatra</th>
                <th className="border px-3 py-2">Degree</th>
              </tr>
            </thead>

            <tbody>
              {Array.from({ length: 12 }, (_, i) => {
                const row = dasamsa.find((d: any) => d.house === i + 1) ?? {
                  house: i + 1,
                  planets: [],
                  sign: '',
                  nakshatra: [],
                  degree: [],
                };

                return (
                  <tr
                    key={row.house}
                    className={
                      i % 2 === 0 ? 'bg-white' : 'bg-[linear-gradient(to_right,#B7E2FF,#2DAAFD)]'
                    }
                  >
                    <td className="border px-3 py-2 text-center !whitespace-pre-line leading-relaxed border-black">
                      {row.planets.length
                        ? row.planets
                            .map(
                              (p: string) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()
                            )
                            .join('\n')
                        : '-'}
                    </td>

                    <td className="border px-3 py-2 text-center border-black">{row.sign || '-'}</td>
                    <td className="border px-3 py-2 text-center border-black">{row.house}</td>
                    <td className="border px-3 py-2 text-center whitespace-pre-line border-black">
                      {row.nakshatra.length ? row.nakshatra.join('\n') : '-'}
                    </td>
                    <td className="border px-3 py-2 text-center whitespace-pre-line border-black">
                      {row.degree.length
                        ? row.degree.map((d: number) => `${d.toFixed(2)}°`).join('\n')
                        : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={selectedTitle}
        widthClass="max-w-3xl"
      >
        <div className="flex flex-col md:flex-row gap-4">
          {modalImages.map((img, i) => (
            <img key={i} src={img} className="w-full rounded-md" />
          ))}
        </div>
      </Modal>
    </div>
  );
}
